import { useState } from 'react';
import { parseUnits, maxUint256 } from 'viem';
import { useWalletClient, usePublicClient } from 'wagmi';
import { useQueryClient } from '@tanstack/react-query';
import { STAKING_CONTRACT, MASTERCHEF_ABI, ERC20_STAKING_ABI } from '../config/staking';
import { toast } from '../utils/toast';

/**
 * Custom hook for staking pool actions (approve, stake, unstake, harvest)
 * Extracted from StakingPoolCard.js for better code organization
 */
export const useStakingActions = ({ 
  pid, 
  poolInfo, 
  address, 
  tokenBalance,
  allowance,
  refetchAllowance 
}) => {
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const queryClient = useQueryClient();

  const [isApproving, setIsApproving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [txHash, setTxHash] = useState(null);

  /**
   * Approve token spending
   */
  const handleApprove = async () => {
    if (!walletClient || !poolInfo) return;

    try {
      setIsApproving(true);
      toast.info('Approving token...');

      const hash = await walletClient.writeContract({
        address: poolInfo.lpToken,
        abi: ERC20_STAKING_ABI,
        functionName: 'approve',
        args: [STAKING_CONTRACT, maxUint256],
      });

      setTxHash(hash);
      await publicClient.waitForTransactionReceipt({ hash });
      
      // Refetch allowance
      await refetchAllowance();
      
      toast.success('Token approved successfully!');
      setIsApproving(false);
      setTxHash(null);
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Approval failed', error.message);
      setIsApproving(false);
      setTxHash(null);
    }
  };

  /**
   * Stake tokens
   */
  const handleStake = async (amount, tokenDecimals) => {
    if (!walletClient || !poolInfo || !amount) return;

    try {
      setIsProcessing(true);
      toast.info('Staking tokens...');

      const parsedAmount = parseUnits(amount.toString(), tokenDecimals);

      const hash = await walletClient.writeContract({
        address: STAKING_CONTRACT,
        abi: MASTERCHEF_ABI,
        functionName: 'stake',
        args: [BigInt(pid), parsedAmount],
      });

      setTxHash(hash);
      await publicClient.waitForTransactionReceipt({ hash });

      // Refetch all data
      queryClient.invalidateQueries({ queryKey: ['staking'] });
      queryClient.invalidateQueries({ queryKey: ['token'] });

      toast.success('Tokens staked successfully!');
      setIsProcessing(false);
      setTxHash(null);
      return true;
    } catch (error) {
      console.error('Stake error:', error);
      
      // Enhanced error logging to capture revert reason
      let errorMessage = 'Staking failed';
      let errorTitle = 'Staking Failed';
      
      // Check for SafeTransferFromFailed error (0xf4059071)
      const errorString = error.toString().toLowerCase();
      if (errorString.includes('0xf4059071') || errorString.includes('safetransferfromfailed')) {
        errorTitle = 'Token Approval Required';
        errorMessage = 'Please approve tokens first before staking. Click the "Approve" button above.';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.reason) {
        errorMessage = error.reason;
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      }
      
      console.error('📛 STAKE ERROR DETAILS:', {
        message: error.message,
        reason: error.reason,
        data: error.data,
        code: error.code,
        errorString: error.toString(),
        fullError: error
      });
      
      toast.error(errorTitle, errorMessage);
      setIsProcessing(false);
      setTxHash(null);
      return false;
    }
  };

  /**
   * Unstake tokens
   */
  const handleUnstake = async (amount, tokenDecimals) => {
    if (!walletClient || !amount) return;

    try {
      setIsProcessing(true);
      toast.info('Unstaking tokens...');

      const parsedAmount = parseUnits(amount.toString(), tokenDecimals);

      const hash = await walletClient.writeContract({
        address: STAKING_CONTRACT,
        abi: MASTERCHEF_ABI,
        functionName: 'unstake',
        args: [BigInt(pid), parsedAmount],
      });

      setTxHash(hash);
      await publicClient.waitForTransactionReceipt({ hash });

      // Refetch all data
      queryClient.invalidateQueries({ queryKey: ['staking'] });
      queryClient.invalidateQueries({ queryKey: ['token'] });

      toast.success('Tokens unstaked successfully!');
      setIsProcessing(false);
      setTxHash(null);
      return true;
    } catch (error) {
      console.error('Unstake error:', error);
      
      // Enhanced error logging to capture revert reason
      let errorMessage = 'Unstaking failed';
      let errorTitle = 'Unstaking Failed';
      
      // Check for SafeTransferFromFailed error (0xf4059071)
      const errorString = error.toString().toLowerCase();
      if (errorString.includes('0xf4059071') || errorString.includes('safetransferfromfailed')) {
        errorTitle = 'Token Approval Required';
        errorMessage = 'Please approve tokens first. Click the "Approve" button.';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.reason) {
        errorMessage = error.reason;
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      }
      
      console.error('📛 UNSTAKE ERROR DETAILS:', {
        message: error.message,
        reason: error.reason,
        data: error.data,
        code: error.code,
        errorString: error.toString(),
        fullError: error
      });
      
      toast.error(errorTitle, errorMessage);
      setIsProcessing(false);
      setTxHash(null);
      return false;
    }
  };

  /**
   * Harvest rewards
   */
  const handleHarvest = async () => {
    if (!walletClient) return;

    try {
      setIsProcessing(true);
      toast.info('Harvesting rewards...');

      const hash = await walletClient.writeContract({
        address: STAKING_CONTRACT,
        abi: MASTERCHEF_ABI,
        functionName: 'harvest',
        args: [BigInt(pid)],
      });

      setTxHash(hash);
      await publicClient.waitForTransactionReceipt({ hash });

      // Refetch all data
      queryClient.invalidateQueries({ queryKey: ['staking'] });
      queryClient.invalidateQueries({ queryKey: ['token'] });

      toast.success('Rewards harvested successfully!');
      setIsProcessing(false);
      setTxHash(null);
      return true;
    } catch (error) {
      console.error('Harvest error:', error);
      
      // Enhanced error logging to capture revert reason
      let errorMessage = 'Harvest failed';
      if (error.message) {
        errorMessage = error.message;
      }
      if (error.reason) {
        errorMessage = error.reason;
      }
      if (error.data?.message) {
        errorMessage = error.data.message;
      }
      
      console.error('📛 HARVEST ERROR DETAILS:', {
        message: error.message,
        reason: error.reason,
        data: error.data,
        code: error.code,
        fullError: error
      });
      
      toast.error('Harvest failed', errorMessage);
      setIsProcessing(false);
      setTxHash(null);
      return false;
    }
  };

  return {
    isApproving,
    isProcessing,
    txHash,
    handleApprove,
    handleStake,
    handleUnstake,
    handleHarvest,
  };
};
